/**
 * KnowledgeSubmissionV1 — the ONE serialized carrier for an org-knowledge
 * proposal (P3, L5 knowledge promotion pipeline).
 *
 * Used on both sides of the lifecycle:
 *   - pending submission body   (L3  pending-knowledge/<scope>/…/<submitter>__<slug>.md)
 *   - the promoted body is the raw markdown (frontmatter stripped) written to
 *     L5  inc-knowledge/<...targetPath>
 *
 * The carrier is a small YAML-ish frontmatter block + the markdown body:
 *
 *   ---
 *   dosia-knowledge-title: <title>
 *   dosia-knowledge-submitter: <userId>
 *   dosia-knowledge-role: <role code>
 *   dosia-knowledge-target: inc-knowledge/proposals/<slug>.md
 *   dosia-knowledge-source: <sourceNote>
 *   dosia-knowledge-origin: <ContentOrigin>
 *   ---
 *   <markdown body>
 *
 * CLAUDE.md §9 single serialization source: every producer MUST go through
 * {@link buildKnowledgeSubmission} (no hand-rolled frontmatter) and every
 * consumer through {@link parseKnowledgeSubmission} (defensive: malformed ⇒
 * null, never a throw). The publish target path is validated by the SINGLE
 * {@link validateKnowledgeTargetPath} (anti-escape, must live under
 * inc-knowledge/) so no caller re-implements the guard.
 */
import { isContentOrigin } from "./content-origin.js";
/** L5 root every knowledge proposal must publish under. */
export const KNOWLEDGE_L5_ROOT = "inc-knowledge";
/** Default publish sub-path when the submitter names none. */
export const KNOWLEDGE_DEFAULT_TARGET_DIR = "inc-knowledge/proposals";
const FIELD = {
    title: "dosia-knowledge-title",
    submitter: "dosia-knowledge-submitter",
    role: "dosia-knowledge-role",
    target: "dosia-knowledge-target",
    source: "dosia-knowledge-source",
    origin: "dosia-knowledge-origin",
};
/** Escape a single-line frontmatter value (newlines would break the block). */
function oneLine(value) {
    return value.replace(/[\r\n]+/g, " ").trim();
}
/**
 * Validate + normalize a knowledge publish target path. Returns the normalized
 * POSIX path (always beginning `inc-knowledge/`) or null when unsafe. Rejects:
 *   - empty
 *   - absolute paths (`/etc/...`)
 *   - any `..` escape or `.` noise once normalized
 *   - anything not rooted at `inc-knowledge/`
 *   - a bare directory (must end in a filename, i.e. have an extension segment)
 */
export function validateKnowledgeTargetPath(raw) {
    if (typeof raw !== "string")
        return null;
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    // Reject absolute + backslash (Windows) up front.
    if (trimmed.startsWith("/") || trimmed.includes("\\"))
        return null;
    // Normalize by resolving segments manually (dependency-free — shared runs in
    // the renderer). Reject any `..` that would escape, and drop `.`.
    const out = [];
    for (const seg of trimmed.split("/")) {
        if (seg === "" || seg === ".")
            continue;
        if (seg === "..")
            return null; // no upward traversal, ever
        // Disallow control chars / NUL that could confuse the store.
        if (/[\u0000-\u001f\u007f]/.test(seg))
            return null;
        out.push(seg);
    }
    if (out.length < 2)
        return null; // must be inc-knowledge/<file>, at least
    if (out[0] !== KNOWLEDGE_L5_ROOT)
        return null;
    const last = out[out.length - 1];
    if (!/\.[a-zA-Z0-9]+$/.test(last))
        return null; // final segment must be a file
    return out.join("/");
}
/** THE builder for a knowledge submission carrier (§9). */
export function buildKnowledgeSubmission(input) {
    if (!isContentOrigin(input.origin)) {
        throw new Error("KnowledgeSubmissionV1.origin 必须是已登记的内容来源");
    }
    const lines = [
        "---",
        `${FIELD.title}: ${oneLine(input.title)}`,
        `${FIELD.submitter}: ${oneLine(input.submitter)}`,
        ...(input.role ? [`${FIELD.role}: ${oneLine(input.role)}`] : []),
        `${FIELD.target}: ${oneLine(input.targetPath)}`,
        ...(input.sourceNote ? [`${FIELD.source}: ${oneLine(input.sourceNote)}`] : []),
        `${FIELD.origin}: ${oneLine(input.origin)}`,
        "---",
        "",
        input.body.trim(),
        "",
    ];
    return lines.join("\n");
}
/** Read one `key: value` line out of a leading `---` frontmatter block. */
function readFrontmatterField(content, key) {
    const fmMatch = /^---\r?\n([\s\S]*?)\r?\n---/.exec(content);
    if (!fmMatch)
        return undefined;
    const re = new RegExp(`^${key}:\\s*(.+?)\\s*$`, "m");
    const m = re.exec(fmMatch[1]);
    return m ? m[1].replace(/^["']|["']$/g, "").trim() : undefined;
}
/**
 * Distinguish "origin line absent" from "origin line present (possibly empty
 * or illegal)". Parse stays non-throwing; callers that must be strict (new
 * submissions) inspect `present`.
 */
function readOriginLine(content) {
    const fmMatch = /^---\r?\n([\s\S]*?)\r?\n---/.exec(content);
    if (!fmMatch)
        return { present: false };
    const re = new RegExp(`^${FIELD.origin}:\\s*(.*?)\\s*$`, "m");
    const m = re.exec(fmMatch[1]);
    if (!m)
        return { present: false };
    return { present: true, raw: m[1].replace(/^["']|["']$/g, "").trim() };
}
/** Strip a leading `---...---` frontmatter block, returning the body. */
function stripFrontmatter(content) {
    const m = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/.exec(content);
    return m ? content.slice(m[0].length).trim() : content.trim();
}
/**
 * THE parser for a knowledge submission carrier (§9). Defensive: returns null
 * for anything without a title + a VALID target path (an unsafe target is
 * treated as malformed so no caller ever publishes outside inc-knowledge/).
 */
export function parseKnowledgeSubmission(content) {
    if (typeof content !== "string" || !content)
        return null;
    const title = readFrontmatterField(content, FIELD.title);
    const submitter = readFrontmatterField(content, FIELD.submitter);
    const targetRaw = readFrontmatterField(content, FIELD.target);
    const target = validateKnowledgeTargetPath(targetRaw);
    if (!title || !submitter || !target)
        return null;
    const role = readFrontmatterField(content, FIELD.role);
    const sourceNote = readFrontmatterField(content, FIELD.source);
    const originLine = readOriginLine(content);
    // Legacy / illegal values: still parse. Missing line and garbage both land
    // on unspecified so promote never throws; submit-time uses originLiteral.
    const origin = originLine.present && isContentOrigin(originLine.raw)
        ? originLine.raw
        : "unspecified";
    const body = stripFrontmatter(content);
    if (!body)
        return null;
    return {
        title,
        submitter,
        ...(role ? { role } : {}),
        targetPath: target,
        ...(sourceNote ? { sourceNote } : {}),
        origin,
        ...(originLine.present ? { originLiteral: originLine.raw } : {}),
        body,
    };
}
//# sourceMappingURL=knowledge-submission.js.map